import localforage from 'localforage';

// Create store instances
const qgdStore = localforage.createInstance({
    name: 'quollDB',
    storeName: 'qgdStore',
    description: 'Stores QGD data with concatenated type/id keys'
});

const thumbtackStore = localforage.createInstance({
    name: 'quollDB',
    storeName: 'thumbtackStore',
    description: 'Stores Thumbtack listings data'
});

const portalStore = localforage.createInstance({
    name: 'quollDB',
    storeName: 'portalStore',
    description: 'Stores portal data'
});

export const QGDStorage = {
    // Initialize database
    async init() {
        try {
            await qgdStore.ready();
            // console.log('QGD storage initialized successfully');
            return true;
        } catch (error) {
            console.error('Failed to initialize QGD storage:', error);
            return false;
        }
    },

    // Store QGD data with type prefix (e.g., 'zip_12345', 'fips_06037')
    async setData(type, id, data) {
        try {
            const key = `${type.toLowerCase()}_${id}`;
            await qgdStore.setItem(key, data);
            // console.log(`Stored QGD data for ${key}`);
            return true;
        } catch (error) {
            console.error(`Failed to store QGD data for ${type}_${id}:`, error);
            return false;
        }
    },

    // Get QGD data by type and id
    async getData(type, id) {
        try {
            
            const key = `${type.toLowerCase()}_${id}`;
            // console.log('Database search for', key);
            const data = await qgdStore.getItem(key);
            // console.log('Database search for', key, 'found', data);
            return data || null;
        } catch (error) {
            console.error(`Failed to get QGD data for ${type}_${id}:`, error);
            return null;
        }
    },

    // Get all data for a specific type (e.g., all ZIP data)
    async getAllByType(type) {
        try {
            const prefix = `${type.toLowerCase()}_`;
            const keys = await qgdStore.keys();
            const typeKeys = keys.filter(key => key.startsWith(prefix));
            
            const results = {};
            for (const key of typeKeys) {
                const id = key.replace(prefix, '');
                const data = await qgdStore.getItem(key);
                results[id] = data;
            }
            return results;
        } catch (error) {
            console.error(`Failed to get all QGD data for type ${type}:`, error);
            return {};
        }
    },

    // Store multiple items at once (useful for initial data load)
    async setBulkData(dataObject) {
        try {
            const operations = [];
            for (const [type, items] of Object.entries(dataObject)) {
                for (const [id, data] of Object.entries(items)) {
                    operations.push(this.setData(type, id, data));
                }
            }
            await Promise.all(operations);
            return true;
        } catch (error) {
            console.error('Failed to store bulk QGD data:', error);
            return false;
        }
    },

    // Clear all QGD data
    async clearAll() {
        try {
            await qgdStore.clear();
            // console.log('Cleared all QGD data');
            return true;
        } catch (error) {
            console.error('Failed to clear QGD data:', error);
            return false;
        }
    }
};

export const ThumbtackStorage = {
    // Initialize database
    async init() {
        try {
            await thumbtackStore.ready();
            return true;
        } catch (error) {
            console.error('Failed to initialize Thumbtack storage:', error);
            return false;
        }
    },

    // Store listing with composite key
    async setListing(zipCode, projectId, data) {
        try {
            const key = `${zipCode}_${projectId}`;
            await thumbtackStore.setItem(key, {
                ...data,
                timestamp: new Date().getTime()
            });
            return true;
        } catch (error) {
            console.error(`Failed to store Thumbtack listing for ${key}:`, error);
            return false;
        }
    },

    // Get listing by zip and project ID
    async getListing(zipCode, projectId) {
        try {
            const key = `${zipCode}_${projectId}`;
            return await thumbtackStore.getItem(key);
        } catch (error) {
            console.error(`Failed to get Thumbtack listing for ${key}:`, error);
            return null;
        }
    },

    // Get all listings for a zip code
    async getListingsByZip(zipCode) {
        try {
            const keys = await thumbtackStore.keys();
            const zipListings = {};
            
            for (const key of keys) {
                if (key.startsWith(`${zipCode}_`)) {
                    const data = await thumbtackStore.getItem(key);
                    const projectId = key.split('_')[1];
                    zipListings[projectId] = data;
                }
            }
            
            return zipListings;
        } catch (error) {
            console.error(`Failed to get Thumbtack listings for zip ${zipCode}:`, error);
            return {};
        }
    },

    // Store multiple listings at once
    async setBulkListings(listings) {
        try {
            const operations = listings.map(listing => 
                this.setListing(listing.zip_code, listing.project_id, listing)
            );
            await Promise.all(operations);
            return true;
        } catch (error) {
            console.error('Failed to store bulk Thumbtack listings:', error);
            return false;
        }
    },

    // Clear all Thumbtack data
    async clearAll() {
        try {
            await thumbtackStore.clear();
            // console.log('Cleared all Thumbtack data');
            return true;
        } catch (error) {
            console.error('Failed to clear Thumbtack data:', error);
            return false;
        }
    }
};

export const PortalStorage = {
    async init() {
        try {
            await portalStore.ready();
            return true;
        } catch (error) {
            console.error('Failed to initialize portal storage:', error);
            return false;
        }
    },

    async setDatapoint(type, data) {
        try {
            await portalStore.setItem(type, {
                data,
                timestamp: new Date().getTime()
            });
            return true;
        } catch (error) {
            console.error(`Failed to store ${type}:`, error);
            return false;
        }
    },

    async getDatapoint(type) {
        try {
            const data = await portalStore.getItem(type);
            return data?.data || null;
        } catch (error) {
            console.error(`Failed to get ${type}:`, error);
            return null;
        }
    },

    async getAllDatapoints() {
        try {
            const types = [
                'house_categories',
                'icons',
                'datapoints',
                'projects',
                'resources',
                'companies',
                'skus',
                'discounts',
                'labor_types'
            ];

            const data = {};
            const missing = [];

            for (const type of types) {
                const result = await this.getDatapoint(type);
                if (result) {
                    data[type] = result;
                } else {
                    missing.push(type);
                }
            }

            return {
                data,
                missing,
                isComplete: missing.length === 0
            };
        } catch (error) {
            console.error('Failed to get all datapoints:', error);
            return {
                data: {},
                missing: [],
                isComplete: false
            };
        }
    },

    // Clear all Portal data
    async clearAll() {
        try {
            await portalStore.clear();
            // console.log('Cleared all Portal data');
            return true;
        } catch (error) {
            console.error('Failed to clear Portal data:', error);
            return false;
        }
    }
};
